import { type FC, memo, useEffect, useState } from 'react'

import { Table } from 'common/components/Table/Table'
import { DEFAULT_TABLE_PARAMS_WITH_SEARCH } from 'common/constants/defaultTableParamsConstants'

import { TableSearchAndFilters } from 'common/components/TableSearchAndFilters/TableSearchAndFilters'
import type { TableParamsType } from 'common/interfaces/ITable'
import type { ObjectType } from 'common/interfaces/object'
import { useParams } from 'react-router'
import { DistinctPatientsTableColumns } from './columns'
import {
  useLazyGetDistinctPatientsQuery,
  useGetDistinctPatientsQuery,
} from 'features/Profile/features/Prescriptions/patient-data.api'
import styles from './patients.module.scss'
import { DialogUploadDistinctPatients, UploadDistinctPatients } from './UploadPatients'
import { Row } from 'antd'

export const DistinctPatients: FC = memo(() => {
  const [columns, setColumns] = useState<ObjectType[]>(DistinctPatientsTableColumns)
  const [tableParams, setTableParams] = useState<TableParamsType>({
    ...DEFAULT_TABLE_PARAMS_WITH_SEARCH,
  })

  const { siteId } = useParams()

  // this is for upload only
  const { data: distinctPatientsData, isFetching: isFetchingDistinctPatients } =
    useGetDistinctPatientsQuery({
      siteId,
      tableParams: {
        current: 1,
        pageSize: 1,
      },
    })

  const [fetchPrescriptionsMapping, { data: prescriptionsData, isFetching }] =
    useLazyGetDistinctPatientsQuery()

  useEffect(() => {
    if (tableParams.sorters) {
      setColumns((prev) =>
        prev.map((column) => ({
          ...column,
          sortOrder: tableParams.sorters?.[column.key]?.order,
        })),
      )
    }
  }, [tableParams.sorters])

  useEffect(() => {
    if (siteId) {
      fetchPrescriptionsMapping({
        siteId,
        tableParams: {
          ...tableParams,
          search: tableParams.debouncedValue?.length >= 2 ? tableParams.debouncedValue : '',
        },
      })
    }
  }, [siteId, tableParams.debouncedValue, tableParams.sorters])

  useEffect(() => {
    if (Number.isInteger(prescriptionsData?.total_items)) {
      setTableParams((prev) => ({ ...prev, total: prescriptionsData.total_items }))
    }
  }, [prescriptionsData?.total_items])

  const hasData = distinctPatientsData?.total_items > 0
  const shouldShowUpload = !isFetchingDistinctPatients && !isFetching && !hasData

  return (
    <div className={styles.parent}>
      <h1 className='page-title'>Prescriptions Mapping</h1>
      <Row justify='space-between' align='top' gutter={[16, 16]}>
        <TableSearchAndFilters
          setTableParams={setTableParams}
          setColumns={setColumns}
          tableParams={tableParams}
          inputHint='Search...'
        />
        <DialogUploadDistinctPatients />
      </Row>
      {shouldShowUpload && (
        <UploadDistinctPatients
          name='file'
          accept='text/csv'
          title='No prescriptions found'
          description='Drag and drop or click to upload patients file in CSV format'
        />
      )}

      {!shouldShowUpload && (
        <Table
          itemKey='id'
          haveActions={false}
          tableParams={tableParams}
          setTableParams={setTableParams}
          dataSource={prescriptionsData?.items}
          columns={columns}
          isLoading={isFetching}
          pagination
        />
      )}
    </div>
  )
})
